/****************************************************
 * Module Name:  watchlist.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Menu, MenuItem, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { FaBoxOpen } from "react-icons/fa";
import { styled, alpha } from "@mui/material/styles";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";

import StoneDetailModal from "../common/stoneDetailModal";
import Utility from "../../utility/utility";
import renderCellExpand from "../common/dataGridCellExpand";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import PacketHistoryModal from "../common/packetHistoryModal";
import CommonStoneSelection from "../common/commonStoneSelection";
import HoldStoneModal from "../operations/holdStoneModal";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import SellMemoModal from "../operations/sellMemoModal";
import CreateMemoModal from "../operations/createMemoModal";
import WatchlistProxy from "../../utility/proxy/manager/watchlistProxy";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import colourchart from '../../images/ColorScheme.png';
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import OfferModal from "../operations/OfferModal";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StoneMediaModal from "../common/stoneMediaModal";
import { LoadingButton } from "@mui/lab";
import UserProxy from "../../utility/proxy/manager/userProxy";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { createExcelFile } from "../common/createExcelFile";
import Download from "@mui/icons-material/Download";
import { TbTableExport } from "react-icons/tb";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  "& .super-app-theme--AVAILABLE": {
    color: "#000",
    "&:hover": {
      color: "#000",
    },
  },
  "& .super-app-theme--HOLD": {
    backgroundColor: "#b6b9d6",
    color: "#000",
    "&:hover": {
      backgroundColor: "#b6b9d6",
      color: "#000",
    },
  },
  "& .super-app-theme--MEMO_OUT": {
    backgroundColor: "#b6b9d6",
    color: "#000",
    "&:hover": {
      backgroundColor: "#b6b9d6",
      color: "#000",
    },
  },
  "& .super-app-theme--SELL": {
    backgroundColor: "#f5d7d7",
    color: "#080808",
    "&:hover": {
      backgroundColor: "#f5d7d7",
      color: "#080808",
    },
  },
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    backgroundColor: 'grey',
  },
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
    py: "8px",
  },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    py: "15px",
  },
  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
    py: "22px",
  },
  "& .redCell": {
    color: "#080808",
    backgroundColor: "#f5d7d7",
  },
  "& .greenCell": {
    color: "#080808",
    backgroundColor: "#B6D7A8"
  },
  "& .yellowCell": {
    color: "#080808",
    backgroundColor: "#F1E68C",
  },

}));

const StyledMenuButtons = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      fontSize: 15,
      padding: "4px 12px",
      '& .MuiSvgIcon-root': {
        fontSize: 16,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

export default function Watchlist() {
  const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
  const [watchlistData, setWatchlistData] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [kapanId, setKapanId] = useState("");
  const [stoneId, setStoneId] = useState("");
  const [packetId, setPacketId] = useState("");
  const [customerId, setCustomerId] = useState(Constants.loggedInUser.usertype !== "C" ? "" : Constants.loggedInUser.id);
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("");
  const [show, setShow] = useState(false);
  const [stockLoader, setStockLoader] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
  const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdData, setHoldData] = useState({});
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [loadingProformaButton, setLoadingProformaButton] = useState(false);
  const [columnNames, setColumnNames] = React.useState([]);

  const [warningStatus, setWarningStatus] = useState([]);
  const [warningStatusMsg, setWarningStatusMsg] = useState([]);

  const [stoneDataMap, setStoneDataMap] = useState({});
  const [showOfferModal, setShowOfferModal] = useState(false);

  const [stoneMediaURL, setStoneMediaURL] = useState([]);
  const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);
  const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

  // Stock Summary
  const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
  const [selectedTotalSize, setSelectedTotalSize] = useState("0");
  const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
  const [selectedavgPrice, setSelectedavgPrice] = useState("0");
  const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");

  const [removeLoader, setRemoveLoader] = useState(false);

  const [tabValue, setTabValue] = useState(0);
  const [activeTab, setActiveTab] = useState("");
  const [showNaturalTab, setShowNaturalTab] = useState(false);
  const [showLabGrownTab, setShowLabGrownTab] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  // eslint-disable-next-line
  const [local_Permission, setLocal_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.OPERATION_STOCK
    )
  );

  // eslint-disable-next-line
  const [holdPermission, setHoldPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTHOLD
    )
  );

  // eslint-disable-next-line
  const [sellPermission, setSellPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTSALE
    )
  );

  // eslint-disable-next-line
  const [offerPermission, setOfferPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECTOFFER
    )
  );

  // eslint-disable-next-line
  const [naturalPermission, setNaturalPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.STONETYPE_NATURAL
    )
  );

  // eslint-disable-next-line
  const [labGrownPermission, setLabGrownPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.STONETYPE_LABGROWN
    )
  );

  // eslint-disable-next-line
  const [fullExcelPermission, setFullExcelPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECT_EXCEL_DOWNLOAD_FULL
    )
  );

  const buttonMinWidth = Utility.isReadAllowed(fullExcelPermission) ? "130px" : "100px";

  useEffect(() => {
    PageTitle("WATCHLIST");
    let showNaturalTab = Utility.isReadAllowed(naturalPermission);
    let showLabGrownTab = Utility.isReadAllowed(labGrownPermission);
    let activeTab = showNaturalTab ? "Natural" : showLabGrownTab ? "Lab Grown" : "Natural";
    setShowNaturalTab(showNaturalTab);
    setShowLabGrownTab(showLabGrownTab);
    setActiveTab(activeTab);
  }, []);

  useEffect(() => {
    // Ensure checkPermission is called after all the state updates

    if (activeTab) {
      setWatchlistData([]);
      loadTotalData([]);
      getAllCustomerData();
      handleSearch();
    }
  }, [activeTab]);

  const [customerData, setCustomerData] = useState({
    customer: Constants.loggedInUser.usertype !== "C" ? "" : { value: Constants.loggedInUser.id, label: Constants.loggedInUser.companyname },
    customerOptions: [],
  });
  const [customerLoader, setCustomerLoader] = useState(false);

  useEffect(() => {
    let stockMap = new Map();
    if (watchlistData.length !== 0) {
      watchlistData.forEach(iData => {
        stockMap.set(iData.stoneid, iData);
      })
      setStoneDataMap(stockMap);
    }
  }, [watchlistData])

  const handleClose = () => {
    setShowStoneDetailModal(false);
    setShowSuccessSnackbar(false);
    setShowPacketDetailModal(false)
    setWarningStatus([]);
  };

  const handleStoneDetailModal = (params) => {
    setStoneId(params.value);
    setShowStoneDetailModal(true);
  };

  const handlePacketDetailModal = (params, kapanid) => {
    setPacketId(params);
    setKapanId(kapanid);
    setShowPacketDetailModal(true);
  };

  const handleSelection = (data) => {
    const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, watchlistData, selectionModel);

    setWatchlistData(tempStockData);
    setSelectionModel(tempSelectionModel);
  }

  const handleSearch = () => {
    setStockLoader(true);
    let userId = Constants.loggedInUser.id;
    WatchlistProxy.ShowWatchlist(
      userId,
      activeTab === "Natural",
      (responseData) => {
        //Success
        if (responseData) {
          const distinctStatus = Utility.getDistinctOptions(responseData, 'status');
          prepareColumns(distinctStatus);
          setWatchlistData(responseData);
          setStockLoader(false);
          loadTotalData(responseData);
        } else {
          // Failure
          setStockLoader(false);
          setErrorSnackbar(true);
        }

      }
    );
  };

  const prepareColumns = (distinctStatus) => {
    let columnNames = [];

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnsData.hasOwnProperty(iColPermission)) {
        const columnData = columnsData[iColPermission];
        if (columnData.field === "status") {
          columnData.type = "singleSelect";
          columnData.valueOptions = distinctStatus;
          columnData.filterOperators = Utility.combinedFilters();
        }
        columnNames.push(columnData);
        if (columnData.field === "stoneid") {
          columnNames.push({
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 200,
            editable: true,
            renderCell: (params) => (
              <Box style={{ display: 'flex', alignItems: 'center', width: "17rem" }}>
                <span style={{ width: "90%", textAlign: "center" }}>{params.value}</span>
                {<EditIcon style={{ marginLeft: 10, fontSize: '1rem' }} />}
              </Box>
            ),
            cellClassName: (params) => 'lavenderCell'
          })
        } else if (columnData.field === "prevppc") {
          columnNames.push({
            field: "currentppc",
            headerName: "CURRENT PPC",
            headerAlign: "center",
            align: "center",
            minWidth: 110,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.currentppc),
            cellClassName: (params) => {
              return (params.row.currentppc === null) ? undefined : (params.row.currentppc === params.row.prevppc) ? null : (params.row.currentppc < params.row.prevppc) ? 'greenCell' : 'redCell';
            }
          })
        }
        else if (columnData.field === "prevdiscount") {
          columnNames.push({
            field: "currentdiscount",
            headerName: "Current Discount",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.currentdiscount),
            cellClassName: (params) => {
              return (params.row.currentdiscount === null) ? undefined : (params.row.currentdiscount === params.row.prevdiscount) ? null : (params.row.currentdiscount < params.row.prevdiscount) ? 'greenCell' : 'redCell';
            }
          })
        } else if (columnData.field === "status" && Constants.loggedInUser.usertype === "E") {
          columnNames.push({
            field: "holdstonecustomercompanyname",
            headerName: "HOLD/MEMO CUSTOMER COMPANY",
            headerAlign: "center",
            align: "center",
            minWidth: 230,
            renderCell: (params) => {
              if (params.row.holdstonecustomerid !== null) {
                let customerCompany = customerData.customerOptions.find((iCustomer) => iCustomer.value === params.row.holdstonecustomerid);
                return customerCompany ? customerCompany.label : " - "
              } else if (params.row.memooutcustomerid !== null) {
                let customerCompany = customerData.customerOptions.find((iCustomer) => iCustomer.value === params.row.memooutcustomerid);
                return customerCompany ? customerCompany.label : " - "
              }
            }
          })
        }
      }
    });
    setColumnNames(columnNames);
  }

  const loadTotalData = (data) => {
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size

    data.forEach((iData) => {
      let pricePerCarat = iData.price
      totalSize += iData.size;
      totalPrice += (iData.size * pricePerCarat);
      total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

    });
    setNoOfSelectedStones(data.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
  };

  useEffect(() => {
    if (selectionModel.length <= 0) {
      loadTotalData(watchlistData);
      return;
    }
    let totalSize = 0;
    let totalPrice = 0;   //ppc*size
    let total = 0;       //rapnetprice*size
    watchlistData.forEach((iData) => {
      if (selectionModel.includes(iData.stoneid)) {
        let pricePerCarat = iData.price
        totalSize += iData.size;
        totalPrice += (iData.size * pricePerCarat);
        total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
      }
    });
    setNoOfSelectedStones(selectionModel.length);
    setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
    setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
    setSelectedavgPrice(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          totalPrice / totalSize
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );
    setSelectedAvgDiscount(
      totalPrice > 0
        ? Utility.ConvertNumberWithSpecifiedDigits(
          (100 - (totalPrice / total) * 100) * -1
        )
        : Utility.ConvertNumberWithSpecifiedDigits(0)
    );

  }, [selectionModel]);

  const checkStoneStatusValidOrNot = () => {
    // let stoneDataArray = [];
    // let differentCustomerid;
    // let flag = false;
    // selectionModel.map(stoneid => {
    //   let iData = stoneDataMap.get(stoneid);
    //   if ((iData.status !== "AVAILABLE" && iData.status !== "MEMO_REQUESTED" && iData.status !== "MEMO_OUT") &&
    //     ((Constants.loggedInUser.usertype === 'E' && iData.holdstoneuserid !== Constants.loggedInUser.id) ||
    //       (Constants.loggedInUser.usertype === 'B' && iData.holdstonebrokerid !== Constants.loggedInUser.id) ||
    //       (Constants.loggedInUser.usertype === 'C' && iData.holdstonecustomerid !== Constants.loggedInUser.id) ||
    //       iData.status === "SELL")) {
    //     stoneDataArray.push(iData.stoneid);
    //   } else if (!flag && iData.holdstonecustomerid !== null) {
    //     differentCustomerid = iData.holdstonecustomerid;
    //     flag = true;
    //   } else if (iData.holdstonecustomerid !== null && differentCustomerid !== iData.holdstonecustomerid) {
    //     stoneDataArray.push(iData.stoneid);
    //   }
    //   return stoneDataArray;
    // })
    // if (stoneDataArray.length === 0) {
    //   setShow(!show);
    // }
    // setWarningStatus(stoneDataArray);

    // Need to Remove the Above Code - As we are no longer checking status for MEMO_REQUEST
    setShow(!show);
  }

  const handleValidation = () => {
    //TODO do validation if needed here
    return true;
  };

  const getAllCustomerData = () => {
    setCustomerLoader(true);
    prepareUserData();
    setCustomerLoader(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(event.target.textContent);
    setTabValue(newValue);
  };

  const getCustomersOfABroker = () => {
    setCustomerLoader(true);
    let permission = activeTab === "Natural" ? "STONETYPE_NATURAL" : "STONETYPE_LABGROWN";
    UserProxy.GetSpecificBrokerCustomerMiniByPermission(Constants.loggedInUser.id, permission, (response) => {
      if (response === undefined) return;

      setCustomersOfABroker(response);
      setCustomerLoader(false);
    });
  };

  const getCustomersOfAEmployee = () => {
    setCustomerLoader(true);
    let permission = activeTab === "Natural" ? "STONETYPE_NATURAL" : "STONETYPE_LABGROWN";
    UserProxy.GetSpecificEmployeesCustomerMiniByPermission(Constants.loggedInUser.id, permission, (response) => {
      if (response === undefined) return;

      setCustomersOfAEmployee(response);
      setCustomerLoader(false);
    });
  };

  const prepareUserData = () => {
    if (Constants.loggedInUser.usertype === "C") {
      if (setCustomerId) {
        setCustomerId({
          value: Constants.loggedInUser.id,
          label: Constants.loggedInUser.companyname,
        });
      }
    } else if (Constants.loggedInUser.usertype === "B") {
      getCustomersOfABroker();
    } else if (Constants.loggedInUser.usertype === "E") {
      getCustomersOfAEmployee();
    }
    return true;
  };

  const setCustomersOfABroker = (response) => {
    let optionsArray = [];
    response.map((data) => {
      let option = { value: data.id, label: data.companyname };
      optionsArray.push(option);
      return true;
    });

    let tempCustomer = undefined;
    if (!customerData.customer) {
      tempCustomer = optionsArray.find(iOptions => iOptions.value === customerData.customer.value);
    }

    setCustomerData({
      ...customerData,
      customerOptions: optionsArray,
      customer:
        !tempCustomer && optionsArray.length > 0
          ? optionsArray[0]
          : customerData.customer,
    });

    setCustomerId(optionsArray.length > 0 ? optionsArray[0] : "");
  };

  const setCustomersOfAEmployee = (response) => {
    let optionsArray = [];
    response.map((data) => {
      let option = { value: data.id, label: data.companyname };
      optionsArray.push(option);
      return true;
    });

    let tempCustomer = undefined;
    if (!customerData.customer) {
      tempCustomer = optionsArray.find(iOptions => iOptions.value === customerData.customer.value);
    }

    setCustomerData({
      ...customerData,
      customerOptions: optionsArray,
      customer:
        !tempCustomer && optionsArray.length > 0
          ? optionsArray[0]
          : customerData.customer,
    });

    setCustomerId(optionsArray.length > 0 ? optionsArray[0] : "");
  };

  const createOffer = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.CreateOffer(data, usertype, (responseData) => {
      if (responseData) {
        handleSearch();
        setShowSuccessSnackbar(true);
        setSuccessMessage("Offer created successfully");
        setSnackbarVariant("success");
        setShowOfferModal(false)
        setLoadingButton(false);
      } else {
        setShowSuccessSnackbar(true);
        setShowOfferModal(false)
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const createMemo = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.CreateMemo(data, usertype, (responseData) => {
      if (responseData && responseData.status === "Success") {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Memo created successfully");
        setSnackbarVariant("success");
        setShow(false);
        setLoadingButton(false);
        handleSearch();
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const holdStones = (data) => {
    setLoadingButton(true);
    const usertype = Constants.loggedInUser.usertype;
    StoneProxy.HoldStones(data, usertype, (responseData) => {
      if (responseData && responseData.status === "Success") {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Hold Successful");
        setSnackbarVariant("success");
        setShowHoldModal(false);
        setLoadingButton(false);
        handleSearch();
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
        setShowHoldModal(false);
      }
    });
  };

  const sellStones = (data) => {
    setLoadingButton(true);
    StoneProxy.SellStonesInvoice(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stones sold successfully");
        setSnackbarVariant("success");
        setShowSellModal(false);
        setLoadingButton(false);
        handleSearch();
      } else {
        setShowSellModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingButton(false);
      }
    });
  };

  const createProforma = (data) => {
    setLoadingProformaButton(true);
    StoneProxy.CreatePerforma(data, (responseData) => {
      if (responseData && responseData.status === "Success") {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Proforma created successfully");
        setSnackbarVariant("success");
        setShowSellModal(false);
        handleSearch();
        setLoadingProformaButton(false);
      } else {
        setShowSellModal(false);
        setShowSuccessSnackbar(true);
        setSuccessMessage(responseData ? responseData.errorMessage : Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setLoadingProformaButton(false);
      }
    });
  };

  const removeFromWatchlist = () => {
    setRemoveLoader(true);
    let userId = Constants.loggedInUser.id;
    let selectedData = [];
    selectionModel.map((stoneId) => {
      selectedData.push({ userid: userId, stoneid: stoneId });
      return true;
    })
    WatchlistProxy.RemoveFromWatchlist(selectedData, (responseData) => {
      if (responseData) {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stone Removed successfully");
        setSnackbarVariant("success");
        handleSearch();
        setRemoveLoader(false);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setRemoveLoader(false);
      }
    })
  }

  const updateWatchlistRemark = () => {
    setRemoveLoader(true);
    let data = watchlistData.filter(iStone => selectionModel.includes(iStone.stoneid))
      .map(iData => ({
        userid: Constants.loggedInUser.id,
        stoneid: iData.stoneid,
        remark: iData.remark
      }));
    WatchlistProxy.UpdateWatchlistRemarks(data, (responseData) => {
      if (responseData) {
        setShowSuccessSnackbar(true);
        setSuccessMessage("Stone Removed successfully");
        setSnackbarVariant("success");
        handleSearch();
        setRemoveLoader(false);
      } else {
        setShowSuccessSnackbar(true);
        setSuccessMessage(Constants.APiFailErrorMessage);
        setSnackbarVariant("error");
        setRemoveLoader(false);
      }
    })
  }

  const getSummaryData = () => {

    let data = [];
    if (selectionModel && selectionModel.length > 0) {
      data = watchlistData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = watchlistData;
    }

    let noOfStones = 0;
    let totalSize = 0;
    let totalAmount = 0;
    let total = 0;

    data.forEach(item => {
      noOfStones += 1;

      if (Utility.isValueValid(item.size)) {
        totalSize += Number(item.size);
      }

      if (Utility.isValueValid(item.total)) {
        totalAmount += Number(item.total);
      }

      if (
        Utility.isValueValid(item.price) &&
        Utility.isValueValid(item.discount) &&
        Utility.isValueValid(item.size)
      ) {
        const effectivePrice = (100 * Number(item.price)) / (100 + Number(item.discount));
        total += Number(item.size) * effectivePrice;
      }
    });

    return {
      ID: noOfStones,
      CTS: (Number(totalSize)).toFixed(2),
      DIS: (Number((100 - (totalAmount / total) * 100) * -1)).toFixed(2),
      PPC: (Number(totalAmount / totalSize)).toFixed(2),
      TOTAL: (Number(totalAmount)).toFixed(2),
    };
  }

  const getExcelData = (fullExcel) => {
    let data = [];

    if (selectionModel && selectionModel.length > 0) {
      data = watchlistData.filter((data) => selectionModel.includes(data.stoneid));
    } else {
      data = watchlistData;
    }

    const excelData = data.map(item => {

      return {
        REPORT: item.certificateid,
        SHAPE: item.shape,
        ID: item.stoneid,
        CTS: item.size,
        COL: item.colortype === "WHITE" ? item.color : item.colordescriptions,
        CLA: item.clarity,
        CUT: item.cut ? item.cut : " ",
        POL: item.pol,
        SYM: item.sym,
        FLR: item.flo,
        MEASUREMENTS: item.measurements,
        DEP: !item.depthpercent ? "" : (Number(item.depthpercent)).toFixed(2),
        "TABLE%": !item.tablepercent ? "" : (Number(item.tablepercent)).toFixed(2),
        "CR ANG": !item.crownangle ? "" : (Number(item.crownangle)).toFixed(2),
        LAB: item.lab,
        RAP: item.rapnetprice,
        DIS: parseFloat((Number(item.discount)).toFixed(2)),
        PPC: parseFloat((Number(item.price)).toFixed(2)),
        TOTAL: parseFloat((Number(item.total)).toFixed(2)),
        LOCATION: item.location,
        STATUS: item.status,
        KEYTOSYMBOLS: item.keytosymbols ? item.keytosymbols : " ",
        MEDIA: {
          t: "s",
          v: "Media URL",
          l: {
            Target: item.stonemediaurl,
            Tooltip: "Click to open images & videos",
          },
        },
        status: item.status ? item.status : "",
        holdstonecustomerid: item.holdstonecustomerid,
        holdstoneuserid: item.holdstoneuserid,
        holdstonebrokerid: item.holdstonebrokerid,
        TABLEINC: item.tableinc ? item.tableinc : "",
        SIDEINC: item.sideinc ? item.sideinc : "",
        H_AND_A: item.h_and_a ? item.h_and_a : "",
        EYECLEAN: item.eyeclean ? item.eyeclean : "",
        TINGE: item.tinge ? item.tinge : "",
        MILKY: item.milky ? item.milky : "",
        OPEN: item.open ? item.open : "",
        LUSTER: item.luster ? item.luster : "",
        TABLEBLACK: item.tableblack ? "YES" : "NO",
        SIDEBLACK: item.sideblack ? "YES" : "NO",
      };
    });

    const calculatedRow = getSummaryData();

    //addding last row for summary data
    const lastRow = Object.fromEntries(
      Constants.STOCK_EXCEL_KEYS.map(key => [key, calculatedRow[key] || ""])
    );

    //addding last row for summary data
    excelData.push(lastRow);

    const exportColumns = fullExcel
      ? [...Constants.STOCK_EXCEL_COLUMNS, ...Constants.EXCEL_OPTIONAL_COLUMNS]
      : Constants.STOCK_EXCEL_COLUMNS;

    createExcelFile(excelData, customerId, exportColumns, "PI-Stock.xlsx");
  }

  const processRowUpdate = (newRow, oldRow) => {
    const changedFeilds = Utility.getChangedFields(newRow, oldRow);
    if (changedFeilds.length > 0) {
      const { id, field, value } = changedFeilds[0];

      let updatedRow;
      const updateWatchlistData = watchlistData.map((iStone) => {
        if (iStone.stoneid === id && value) {
          if (field === "remark") {
            updatedRow = {
              ...iStone,
              [field]: value,
            };
          }
          return updatedRow;
        }
        return iStone;
      })
      setWatchlistData(updateWatchlistData);
      return updatedRow;
    } else {
      // No changes detected, return the newRow to trigger exit edit mode
      return { ...newRow };
    }
  };

  const columnsData = {
    "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
      field: "stoneid",
      headerName: "STONEID",
      width: 80,
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
        <Button
          sx={{
            textDecorationLine: "underline",
            color: 'black'
          }}
          onClick={() => handleStoneDetailModal(params)}
        >
          {params.value}
        </Button>
        :
        <div>{params.value}</div>,
    },
    "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
      field: "kapanid",
      headerName: "KAPANID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
        <Button
          sx={{
            textDecorationLine: "underline",
            color: 'black',
          }}
          onClick={() => {
            setKapanId(params.row.kapanid);
            setShowKapanDetailModal(true);
          }}
        >
          {params.value}
        </Button>
        :
        <div>{params.value}</div>,
      width: 80,
    },
    "8a492e00-6167-45de-b078-e572e47c8a39": {
      field: "packetid",
      headerName: "PACKETID",
      headerAlign: "center",
      align: "center",
      width: 80,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => {
        if (params.value === null || params.row.kapanid === null) {
          return;
        } else {
          return Utility.NeedToShowPacketDetailModal() ? (
            <Button
              sx={{ textDecorationLine: "underline", color: "black" }}
              onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
            >
              {params.value}
            </Button>
          ) : (
            <div>{params.value}</div>
          );
        }
      }
    },
    "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
      field: "status",
      headerName: "STATUS",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: renderCellExpand,
      cellClassName: (params) =>
        (Constants.loggedInUser.usertype === 'E' && params.row.holdstonecustomerid !== null && customerData.customerOptions.find(
          (iCustomer) =>
            iCustomer.value === params.row.holdstonecustomerid
        )) && params.row.status === "HOLD" ? 'greenCell' :
          (Constants.loggedInUser.usertype === "C" && params.row.holdstoneuserid === Constants.loggedInUser.id && params.row.status === "HOLD") ? 'greenCell' :
            (Constants.loggedInUser.usertype === "E" && params.row.memooutcustomerid !== null && customerData.customerOptions.find(
              (iCustomer) =>
                iCustomer.value === params.row.memooutcustomerid
            ) && params.row.status === "MEMO_OUT") ? 'greenCell' :
              (Constants.loggedInUser.usertype === "C" && params.row.memooutuserid === Constants.loggedInUser.id && params.row.status === "MEMO_OUT") ? 'greenCell' : `super-app-theme--${params.row.status.toUpperCase()}`,
    },
    "39c634f1-2644-4307-acbb-983b4de4229e": {
      field: "prevppc",
      headerName: "PREVIOUS PPC",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.prevppc)
    },
    "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
      field: "stonetype",
      headerName: "STONETYPE",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: activeTab === "Natural" ? ["Natural"] : Constants.uniqueFilterValues.stonetype.filter(option => option.name !== 'N').map(option => option.fullname),
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => Constants.STONE_TYPE[params.value],
      renderCell: (params) => Constants.STONE_TYPE[params.value],
    },
    "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
    },
    "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
      field: "total",
      headerName: "TOTAL",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
    },
    "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
      field: "clarity",
      headerName: "CLARITY",
      headerAlign: "center",
      align: "center",
      width: 75,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
      field: "color",
      headerName: "COLOR",
      headerAlign: "center",
      align: "center",
      width: 65,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "d4992de9-92ab-4f9e-ba12-6c746e935611": {
      field: "colordescriptions",
      headerName: "COLOR DESCRIPTIONS",
      headerAlign: "center",
      align: "center",
      width: 160,
      renderCell: renderCellExpand,
    },
    "3597803f-8932-40c6-8de6-81006f4d6d5b": {
      field: "cut",
      headerName: "CUT",
      headerAlign: "center",
      align: "center",
      width: 50,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
      field: "createdat",
      headerName: "CREATED AT",
      headerAlign: "center",
      align: "center",
      minWidth: 190,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    "8296e627-0be5-4a66-830f-ea6867cea137": {
      field: "flo",
      headerName: "FLO",
      headerAlign: "center",
      align: "center",
      width: 60,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
      field: "lab",
      headerName: "LAB",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "19ce7030-402c-495c-84ac-a29093bdb0ac": {
      field: "locationid",
      headerName: "LOCATION",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "ab3e506e-bddb-456d-b967-922dcee74d56": {
      field: "location",
      headerName: "LOCATION",
      headerAlign: "center",
      align: "left",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 130,
      renderCell: (params) => params.row.stoneoldstatus === null ? params.value : (
        <Box
          sx={{
            "& .MuiBadge-badge": {
              height: "0.75rem !important",
              padding: "2px 4px !important",
              fontSize: "8px",
              top: "-1px",
              right: "-20px",
              backgroundColor: "#E5D9F2 !important",
              color: "#624E88 !important",
              fontWeight: "700"
            },
          }}
        >
          <Badge
            badgeContent={"TRANSIT"}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {params.value}
          </Badge>
        </Box>
      ),
    },
    "f97a0118-3891-4f77-abf7-8636c26d594c": {
      field: "country",
      headerName: "COUNTRY",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
      field: "pol",
      headerName: "POL",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
      field: "quality",
      headerName: "QUALITY",
      headerAlign: "center",
      align: "center",
      width: 80,
      renderCell: renderCellExpand,
    },
    "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
      field: "shade",
      headerName: "SHADE",
      headerAlign: "center",
      align: "center",
      width: 60,
      renderCell: renderCellExpand,
    },
    "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
      field: "shape",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      width: 80,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "5541c182-21b8-496e-b342-b0b89f3d302f": {
      field: "sym",
      headerName: "SYM",
      headerAlign: "center",
      align: "center",
      width: 40,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "798a8663-0f0c-41be-8512-16dd60d5f25e": {
      field: "prevdiscount",
      headerName: "PREVIOUS DIS",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "567202e6-c31c-4c19-9998-1cacae05af6e": {
      field: "depth",
      headerName: "DEPTH",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
      field: "height",
      headerName: "HEIGHT",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
      field: "width",
      headerName: "WIDTH",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "8eaa8a03-d163-436d-8912-51ea06306f44": {
      field: "certificateid",
      headerName: "CERT",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
      field: "comment",
      headerName: "COMMENT",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "e7210f4a-6e0a-4591-8428-e6845004ed04": {
      field: "crownheight",
      headerName: "CR.HEIGHT",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "c4e65984-1682-4c1e-812b-8938e9078b2d": {
      field: "crownangle",
      headerName: "CR.ANG",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
      field: "culetsize",
      headerName: "CULET SZ",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "e3f77521-2c57-46bc-a02b-3c884685d2be": {
      field: "culetcondition",
      headerName: "CULET",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
      field: "depthpercent",
      headerName: "DEPTH%",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "e83c2978-9088-49f0-9dc1-bcced73025fd": {
      field: "gridlemin",
      headerName: "GIRDLEMIN",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
      field: "intensitylongtitle",
      headerName: "INTENSITYLONGTITLE",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "06d2a589-0d42-4dde-8724-acf2448c839a": {
      field: "laserinscription",
      headerName: "INS",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
      field: "measurements",
      headerName: "MEASUREMENTS",
      headerAlign: "center",
      align: "center",
      width: 140,
      renderCell: renderCellExpand,
    },
    "dd934505-1865-48cb-878f-f5d3c18af2d5": {
      field: "note",
      headerName: "NOTE",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
      field: "paviliondepth",
      headerName: "PV.DEPTH",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
      field: "tablepercent",
      headerName: "TABLE%",
      headerAlign: "center",
      align: "center",
      width: 75,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "db078198-4416-4d53-b3d0-70d3a5418f65": {
      field: "open",
      headerName: "OPEN",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "e9028170-e978-4978-a2c9-1889c1821495": {
      field: "gridlecondition",
      headerName: "GIRDLE COND",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
      field: "gridlepercent",
      headerName: "GIRDLE%",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
      field: "pavilionangle",
      headerName: "PV.ANGLE",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
      field: "white",
      headerName: "WHITE",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      renderCell: renderCellExpand,
    },
    "cf694b75-ba5d-4a73-8fcf-e7566974c4b6": {
      field: "media",
      headerName: "MEDIA",
      headerAlign: "center",
      align: "center",
      width: 60,
      renderCell: (params) => (
        <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
          <VideoLibraryTwoToneIcon
            sx={{ cursor: "pointer" }}
            onClick={() => {
              setStoneMediaURL(params.row.stonemediaurl);
              setShowStoneMediaModal(true);
            }} />
        </Tooltip>
      ),
    },
    "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
      field: "rapnetprice",
      headerName: "RAP",
      headerAlign: "center",
      align: "right",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      editable: true,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
    },
  };

  const rows = watchlistData;
  const columns = columnNames;

  // Color Scheme Drop Down Button
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseButton = () => {
    setAnchorEl(null);
  };

  //Custom Top Menu Bar
  function CustomToolbar() {
    const [excelMenu, setExcelMenu] = useState(undefined);
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <CommonStoneSelection
          title={"Enter multiple Stone Ids"}
          handleSelection={handleSelection}
          buttonDisabled={rows.length === 0}
        />

        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
        {/* Custom Buttons */}
        {stockLoader ? (
          <CircularProgress size="1rem" />
        ) : (
          <Tooltip title="Reload List">
            <RefreshOutlinedIcon
              onClick={() => handleSearch()}
              color="primary"
              fontSize="medium"
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        )}
        {!Utility.isReadAllowed(fullExcelPermission) ? (
          <Tooltip title="Export">
            <Button
              component="label"
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<Download />}
              onClick={() => {
                getExcelData(false);
              }}
            >
              EXCEL
            </Button></Tooltip>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginLeft: "5px",
              "& .MuiButton-startIcon": {
                marginRight: "0px !important",
                marginLeft: "-2px",
              },
              "& .MuiLoadingButton-loadingIndicator": { left: "7px" },
            }}
          >
            <LoadingButton
              id="demo-customized-export-menu"
              aria-controls={
                excelMenu ? "demo-customized-export-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={excelMenu ? "true" : undefined}
              variant="outlined"
              disableElevation
              onClick={(e) => setExcelMenu(e.currentTarget)}
              sx={{
                color: "#2e7d32",
                borderRadius: "4px",
                border: "1px solid rgba(46, 125, 50, 0.5)",
                padding: "2px 2px 2px 8px",
                cursor: "pointer",
              }}
              loadingPosition="start"
              startIcon={<TbTableExport size={18} />}
            >
              <span style={{ fontSize: "14px", marginLeft: "4px" }}>EXPORT</span>
              <KeyboardArrowDownIcon />
            </LoadingButton>

            <StyledMenuButtons
              id="demo-customized-export-menu"
              MenuListProps={{
                "aria-labelledby": "demo-customized-export-menu",
                style: {
                  maxHeight: 250,
                },
              }}
              anchorEl={excelMenu}
              open={excelMenu}
              onClose={() => setExcelMenu(null)}
            >
              <MenuItem>
                {Utility.isReadAllowed(fullExcelPermission) &&
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    color="secondary"
                    sx={{
                      ml: "5px",
                      minWidth: buttonMinWidth,
                      justifyContent: "flex-start",
                      textAlign: "left",
                      "& .MuiButton-startIcon": {
                        marginRight: "1px",
                        marginLeft: "0px",
                      },
                    }}
                    startIcon={<Download />}
                    onClick={() => {
                      getExcelData(true);
                      setExcelMenu(null);
                    }}
                  >
                    FULL EXCEL
                  </Button>
                }
              </MenuItem>
              <MenuItem>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  color="secondary"
                  sx={{
                    ml: "5px",
                    minWidth: buttonMinWidth,
                    justifyContent: "flex-start",
                    textAlign: "left",
                    "& .MuiButton-startIcon": {
                      marginRight: "1px",
                      marginLeft: "0px",
                    },
                  }}
                  startIcon={<Download />}
                  onClick={() => {
                    getExcelData(false);
                    setExcelMenu(null);
                  }}
                >
                  EXCEL
                </Button>
              </MenuItem>
            </StyledMenuButtons>
          </Box>
        )}

        {Utility.isReadAllowed(offerPermission) ? (
          <Tooltip title="Offer">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<LocalOfferIcon size="large" />}
              color="success"
              disabled={selectionModel.length === 0 || stockLoader}
              onClick={() => { setShowOfferModal(true) }}>
              OFFER
            </Button>
          </Tooltip>
        ) : null}



        {Utility.isReadAllowed(holdPermission) ? (
          <Tooltip title="Hold Stones">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<FaBoxOpen />}
              color="success"
              disabled={!(selectionModel.length > 0 && (Constants.loggedInUser.usertype === "C" || customerData.customer)) || stockLoader}
              onClick={() => {
                let stoneDataArray = [];
                let data = [];
                selectionModel.map(stoneid => {
                  let iData = stoneDataMap.get(stoneid);
                  data.push({
                    id: iData.stoneid,
                    status: iData.status
                  })
                  if ((iData.status !== "AVAILABLE" && iData.status !== "MEMO_OUT") || (iData.status === "MEMO_OUT" && iData.memooutcustomerid !== customerData.customer.value)) {
                    stoneDataArray.push(iData.stoneid);
                  }
                  return stoneDataArray;
                });

                if (stoneDataArray.length === 0) {
                  setShowHoldModal(true);
                  setHoldData(data);
                } else {
                  setWarningStatusMsg("Please select stones that are 'AVAILABLE' or select customers for whom 'MEMO_OUT' has been done.");
                  setWarningStatus(stoneDataArray);
                }
              }}
            >
              Hold
            </Button>
          </Tooltip>
        ) : null}



        {Utility.isReadAllowed(sellPermission) ? (
          <Tooltip title="Sell Stones">
            <Button
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<DiamondOutlinedIcon size="large" />}
              color="success"
              disabled={!(selectionModel.length > 0 && (Constants.loggedInUser.usertype === "C" || customerData.customer)) || stockLoader}
              onClick={() => {
                let stoneDataArray = [];
                selectionModel.map(stoneid => {
                  let iData = stoneDataMap.get(stoneid);
                  if ((iData.status !== "AVAILABLE" && iData.status !== "MEMO_OUT" && iData.status !== "HOLD") ||
                    (iData.status === "MEMO_OUT" && iData.memooutcustomerid !== customerData.customer.value) ||
                    (iData.status === "HOLD" && iData.holdstonecustomerid !== customerData.customer.value)) {
                    stoneDataArray.push(iData.stoneid);
                  }
                  return stoneDataArray;
                })

                if (stoneDataArray.length === 0) {
                  setShowSellModal(true)
                } else {
                  setWarningStatusMsg("Please select stones that are 'AVAILABLE' or select customers for whom 'MEMO_OUT'/'HOLD' has been done.");
                  setWarningStatus(stoneDataArray);
                }
              }}
            >
              {Utility.GetBuyOrSell()}
            </Button>
          </Tooltip>
        ) : null}



        {Utility.isAddAllowed(local_Permission) && activeTab === "Natural" ? (
          <Tooltip title="Create Memo">
            <Button
              color="primary"
              variant="outlined"
              size="small"
              component="label"
              sx={{ ml: "5px" }}
              startIcon={<AddBoxOutlinedIcon size="large" />}
              disabled={!(selectionModel.length > 0 && (Constants.loggedInUser.usertype === "C" || customerData.customer)) || stockLoader}
              onClick={checkStoneStatusValidOrNot}
            >
              Memo
            </Button>
          </Tooltip>
        ) : null}



        <Tooltip title="Update Watchlist Remarks">
          <LoadingButton
            color="primary"
            variant="outlined"
            size="small"
            component="label"
            sx={{ ml: "5px" }}
            disabled={selectionModel.length === 0 || stockLoader}
            onClick={() => updateWatchlistRemark(watchlistData)}
            loading={removeLoader}
            loadingPosition="start"
            type="submit"
            startIcon={<CloudUploadIcon size="large" />}
          >
            Update
          </LoadingButton>
        </Tooltip>

        <Tooltip title="Remove from Watchlist">
          <LoadingButton
            color="error"
            variant="outlined"
            size="small"
            component="label"
            sx={{ ml: "5px" }}
            disabled={selectionModel.length === 0 || stockLoader}
            onClick={() => removeFromWatchlist(watchlistData)}
            loading={removeLoader}
            loadingPosition="start"
            type="submit"
            startIcon={<DeleteOutlineOutlined size="large" />}
          >
            Remove
          </LoadingButton>
        </Tooltip>



        <GridToolbarQuickFilter debounceMs={500} />
      </GridToolbarContainer>
    );
  }

  return (
    <Box>
      <div>
        {showStoneDetailModal ? (
          <StoneDetailModal
            show={showStoneDetailModal}
            stoneId={stoneId}
            handleClose={handleClose}
          />
        ) : null}

        {showStoneMediaModal ? (
          <StoneMediaModal
            show={showStoneMediaModal}
            stoneMediaURL={stoneMediaURL}
            handleClose={() => setShowStoneMediaModal(false)}
          />
        ) : null}

        <CommonSnackbar
          show={errorSnackbar}
          message={Constants.APiFailErrorMessage}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          title={"Failed"}
          handleClose={() => setErrorSnackbar(false)}
        />
        {warningStatus.length !== 0 ? (
          <CommonSnackbar
            show={true}
            message={warningStatusMsg}
            variant={"warning"}
            autoHide={Constants.SnackBarCloseTimer}
            title={"Warning"}
            handleClose={handleClose}
          />
        ) : null}
        {showSuccessSnackbar ? (
          <CommonSnackbar
            show={true}
            message={successMessage}
            variant={snackbarVariant}
            autoHide={Constants.SnackBarCloseTimer}
            title={snackbarVariant === "success" ? "Success" : "Failed"}
            handleClose={handleClose}
          />
        ) : null}
        {showPacketDetailModal ? (
          <PacketHistoryModal
            show={showPacketDetailModal}
            packetId={packetId}
            kapanId={kapanId}
            close={handleClose}
          />
        ) : null}
        {showKapanDetailModal ? (
          <KapanHistoryModal
            show={showKapanDetailModal}
            kapanId={kapanId}
            close={() => setShowKapanDetailModal(false)}
          />
        ) : null}

        {showUserDetailModal ? (
          <UserDetailModal
            show={true}
            handleClose={() => setShowUserDetailModal(false)}
            handleSelect={(selectedOption) => {
              const selectedCustomers = customerData.customerOptions.filter(opt =>
                selectedOption.includes(opt.value)
              );
              setCustomerData({
                ...customerData,
                customer: selectedCustomers[0],
              });
              if (setCustomerId) {
                setCustomerId(selectedCustomers[0]);
              }
              setShowUserDetailModal(false)
            }}
            userids={customerData.customerOptions.filter(option => option.value !== "*").map(cust => cust.value)}
            selectedIds={customerData.customer.length !== 0 ? [customerData.customer.value] : []}
            usertype={"C"}
            loading={showUserDetailModal}
            isMulti={false}
          />
        ) : null}

        {show && (
          <CreateMemoModal
            show={true}
            setShow={setShow}
            handleValidation={handleValidation}
            handleSave={createMemo}
            stoneId={selectionModel}
            customer={customerId}
            loading={loadingButton}
          />
        )}
        {showSellModal && (
          <SellMemoModal
            show={true}
            setShow={setShowSellModal}
            handleValidation={handleValidation}
            handleInvoice={sellStones}
            handleProforma={createProforma}
            stoneId={selectionModel}
            customer={customerId}
            user={
              Constants.loggedInUser.id +
              " | " +
              Constants.loggedInUser.companyname
            }
            proformaButton={true}
            loading={loadingButton}
            loadingProformaButton={loadingProformaButton}
          />
        )}

        {showHoldModal ? (
          <HoldStoneModal
            show={true}
            setShow={setShowHoldModal}
            handleValidation={handleValidation}
            handleSave={holdStones}
            data={holdData}
            stoneId={selectionModel}
            customer={customerData.customer}
            loading={loadingButton}
          />
        ) : null}

        {showOfferModal ? (
          <OfferModal
            show={true}
            setShow={setShowOfferModal}
            handleValidation={handleValidation}
            handleOffer={createOffer}
            stoneId={selectionModel}
            customer={customerId}
            user={Constants.loggedInUser.id + " | " + Constants.loggedInUser.companyname}
            loadingOfferButton={loadingButton}
            notPermitted={false}
          />
        ) : null}
      </div>

      <Box>
        <Box style={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', marginTop: "5px", }}>
          {showNaturalTab && showLabGrownTab && (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": {
                  height: "3px",
                  bottom: "5px",
                },
                "& .MuiTabs-flexContainer": {
                  marginLeft: "15px",

                },
              }}
            >
              <Tab label="Natural" />
              <Tab label="Lab Grown" />
            </Tabs>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              flexBasis: 0,
              overflow: "hidden",
              color: "black",
              fontWeight: "normal",
              px: 1,
              textAlign: "center",
              paddingTop: "5px",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                justifyContent: "center",
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: Constants.defaultTableFontSize }}
              >
                No of Stones: <b>{noOfSelectedStones}</b>
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: Constants.defaultTableFontSize }}
              >
                Total Weight: <b>{selectedTotalSize}</b>
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: Constants.defaultTableFontSize }}
              >
                Total Price: <b>{selectedtotalPrice}</b>
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: Constants.defaultTableFontSize }}
              >
                Avg Price: <b>{selectedavgPrice}</b>
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: Constants.defaultTableFontSize }}
              >
                Avg Discount: <b>{selectedAvgDiscount}</b>
              </Typography>
            </Stack>
          </Box>

          <div>
            <Box
              id="demo-customized-button"
              aria-controls={open ? "demo-customized-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              variant="outlined"
              disableElevation
              onClick={handleClick}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                color: "#454545",
                borderColor: "#454545",
                borderRadius: "4px",
                border: "1px solid black",
                padding: "5px 0 5px 5px",
                cursor: "pointer",
              }}
            >
              <img
                src={colourchart}
                alt="Color Scheme"
                style={{
                  height: "24px",
                  width: "24px",
                }}
              />
              <KeyboardArrowDownIcon />
            </Box>
            <StyledMenu
              id="demo-customized-menu"
              MenuListProps={{
                "aria-labelledby": "demo-customized-button",
                style: {
                  maxHeight: 250,
                },
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseButton}
            >
              <MenuItem onClick={handleCloseButton} disableRipple>
                <span
                  style={{
                    height: "15px",
                    width: "15px",
                    backgroundColor: "#000",
                    borderRadius: 20,
                    margin: 10,
                  }}
                ></span>
                Available
              </MenuItem>
              <MenuItem onClick={handleCloseButton} disableRipple>
                <span
                  style={{
                    height: "15px",
                    width: "15px",
                    backgroundColor: "#b6b9d6",
                    borderRadius: 20,
                    margin: 10,
                  }}
                ></span>
                Hold (Others)
              </MenuItem>
              <MenuItem onClick={handleCloseButton} disableRipple>
                <span
                  style={{
                    height: "15px",
                    width: "15px",
                    backgroundColor: "#B6D7A8",
                    borderRadius: 20,
                    margin: 10,
                  }}
                ></span>{" "}
                {Constants.loggedInUser.usertype === "E"
                  ? "Hold (My Customers)"
                  : " Hold (Self)"}
              </MenuItem>
              <MenuItem onClick={handleCloseButton} disableRipple>
                <span
                  style={{
                    height: "15px",
                    width: "15px",
                    backgroundColor: "#D4EFF8",
                    borderRadius: 20,
                    margin: 10,
                  }}
                ></span>
                Memo Out (Others)
              </MenuItem>
              <MenuItem onClick={handleCloseButton} disableRipple>
                <span
                  style={{
                    height: "15px",
                    width: "15px",
                    backgroundColor: "#CAF7F6",
                    borderRadius: 20,
                    margin: 10,
                  }}
                ></span>
                {Constants.loggedInUser.usertype === "E"
                  ? "Memo Out (My Customers)"
                  : "Memo Out (Self)"}
              </MenuItem>
              <MenuItem onClick={handleCloseButton} disableRipple>
                <span
                  style={{
                    height: "15px",
                    width: "15px",
                    backgroundColor: "#FEBD7D",
                    borderRadius: 20,
                    margin: 10,
                  }}
                ></span>
                AVAILABLE (IN_TRANSIT)
              </MenuItem>
            </StyledMenu>
          </div>
        </Box>


        <Box display={"flex"} m={1} flexDirection={"row"} justifyContent={"space-between"} alignItems={"center"}>


          {Constants.loggedInUser.usertype !== "C" && (

            <Box sx={{ display: "flex", gap: "10px", alignItems: "center", flex: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Reload List">
                {customerLoader ? (
                  <CircularProgress size="1rem" />
                ) : (
                  <RefreshOutlinedIcon
                    color="primary"
                    fontSize="medium"
                    sx={{ cursor: "pointer" }}
                    onClick={
                      Constants.loggedInUser.usertype === "B"
                        ? getCustomersOfABroker
                        : Constants.loggedInUser.usertype === "E"
                          ? getCustomersOfAEmployee
                          : getAllCustomerData
                    }
                  />
                )}
              </Tooltip>

              <Typography>Customer Company </Typography>

              <Box sx={{
                gap: "5px",
                display: "flex",
                alignItems: "center",
                maxWidth: "40vw",
              }}>
                <Select
                  menuPlacement="auto"
                  blurInputOnSelect={false}
                  className="basic-single"
                  classNamePrefix="select"
                  value={customerData.customerOptions.find(
                    (iCustomer) =>
                      iCustomer.value === customerData.customer.value
                  )}
                  isSearchable={true}
                  name="Customer"
                  menuPortalTarget={document.body}
                  styles={{
                    ...Constants.Styles,
                    container: (base) => ({
                      ...base,
                      minWidth: "100px",
                    }),
                  }}
                  options={customerData.customerOptions}
                  onChange={(selectedOption) => {
                    setCustomerData({
                      ...customerData,
                      customer: selectedOption,
                    });
                    if (setCustomerId) {
                      setCustomerId(selectedOption);
                    }
                  }}
                  isDisabled={selectionModel.length > 0 ? false : true}
                />
              </Box>
              <Tooltip title="View More Data">
                <OpenInNewIcon
                  fontSize="medium"
                  color="action"
                  sx={{ cursor: "pointer" }}
                  onClick={selectionModel.length > 0 ? () => setShowUserDetailModal(true) : undefined}
                />
              </Tooltip>


            </Box>
          )}

        </ Box>
      </Box>

      <Box
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          width: "100%",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .lavenderCell": {
            color: "#080808",
            backgroundColor: "#D8BFD8",
          },
          height: showNaturalTab && showLabGrownTab ? "73vh" : "80vh",
        }}
      >
        <StyledDataGrid
          disableRowSelectionOnClick={true}
          localeText={{ noRowsLabel: "No Record Found" }}
          sx={{ fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
          slots={{
            toolbar: CustomToolbar,
          }}
          rows={rows}
          density="compact"
          columns={columns}
          getRowId={(row) => row.stoneid}
          checkboxSelection={true}
          onRowSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          loading={stockLoader}
          rowSelectionModel={selectionModel}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns id, the other columns will remain visible
                locationid: false,
                kapanid: false
              },
            },
            pagination: {
              paginationModel: Constants.pageSize,
            },
          }}
          pageSizeOptions={Constants.pageSizeOptions}
          pagination
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => {
            // console.error(error);
          }}
          apiRef={apiRef}
          onCellClick={handleCellClick}
          editMode="cell"
        />
      </Box>
    </Box>
  )
}  